import { useOverlayPortalRoot } from "@/components/modal/overlay.hooks";
import { COMMUNITY_SLACK_URL } from "@/utils/community-support";
import { usePylonEnabled } from "@/utils/hooks/use-pylon-enabled";
import { useAgentConversationBreadcrumbLabels } from "@/components/agent-conversation/agent-conversation-breadcrumbs.hooks";
import { useComputeBreadcrumbLabels } from "@/components/compute/compute-breadcrumbs.hooks";
import { useDashboardBreadcrumbLabels } from "@/components/dashboards/dashboards.hooks";
import { useDatasetBreadcrumbLabels } from "@/components/dataset/dataset.hooks";
import {
  useEvaluationBreadcrumbData,
  type EvaluationBreadcrumbData,
} from "@/components/evaluations/evaluation-breadcrumbs.hooks";
import {
  useNotebookBreadcrumbData,
  type NotebookBreadcrumbData,
} from "@/components/notebook/notebook-breadcrumbs.hooks";
import { Icon, IconName } from "@/components/icons/Icon";
import { BreadcrumbV2Item, BreadcrumbsV2 } from "@/components/interactive/BreadcrumbsV2";
import { ButtonV2, IconButtonV2, LinkButtonV2 } from "@/components/interactive/ButtonV2";
import { KeyboardShortcutV2 } from "@/components/interactive/KeyboardShortcutV2";
import { MenuContainerV2, MenuSectionV2 } from "@/components/interactive/MenuContainerV2";
import { MenuItemV2, type MenuItemV2Variant } from "@/components/interactive/MenuItemV2";
import { ViewportAwareShortcutTooltipV2 } from "@/components/interactive/ShortcutTooltipV2";
import { ViewportAwareFixedMenuV2 } from "@/components/interactive/ViewportAwareFixedMenuV2";
import { TooltipV2 } from "@/components/TooltipV2";
import { HRefArguments } from "@/components/navigation/ProjectNavbarCommon.constants";
import {
  CATEGORY_RAIL_COLLAPSE_TRANSITION_CLASS,
  useCategoryRailCollapse,
} from "@/components/navigation/category-rail-collapse";
import { useTransitionAcrossPages } from "@/components/navigation/transition-across-pages";
import { MOTION_DURATION_CLASS } from "@/utils/motion";
import { NavbarMainItem } from "@/components/navigation/navigation.types";
import { getProjectNavigationRouteKey } from "@/components/navigation/project-navigation-route-key";
import {
  PROJECT_NAV_RECENT_ENABLED,
  useRecordProjectNavHistory,
} from "@/components/navigation/project-nav-history";
import { useProjectNavbarLinks } from "@/components/navigation/useProjectNavbarLinks";
import { classNames } from "@/utils/classnames";
import { useRouteAndTeamInfo } from "@/utils/hooks/url/use-route-info";
import {
  type CategoryNavMode,
  useCategoryNavEnabled,
  useCategoryNavOverride,
} from "@/utils/hooks/use-category-nav-enabled";
import { CHALK_ADMIN_SURFACE_STABLE_CLASS } from "@/components/admin/admin-visual-treatment";
import { useDANGEROUSisChalkEmployee } from "@/utils/hooks/use-is-chalk-employee";
import { useSidebarLayoutReset } from "@/components/navigation/project-nav-sidebar-layout";
import { openCommandPalette } from "@/components/command-palette/open-command-palette";
import { NavExperimentCallout } from "@/components/navigation/NavExperimentCallout";
import { createConfirmModal } from "@/components/modal/confirm-modal";
import { parseNavSetup, useNavSetupTransfer } from "@/components/navigation/nav-setup-transfer";
import toast from "react-hot-toast";
import { SliderTabsV2, type SliderTabV2Item } from "@/components/interactive/TabsV2";
import { useFlagsV2 } from "@/utils/hooks/use-flags-grpc";
import { usePagerDutyEscalation } from "@/utils/hooks/use-pagerduty-escalation";
import { useNextEnvVars } from "@/components/context-providers/NextEnvVarsWrapper";
import { ProjectProfileButtonV2 } from "@/components/navigation/ProjectProfileMenuV2";
import {
  ensurePylonWindowCallbacks,
  usePylonChatBubbleControls,
  usePylonWindowOpen,
} from "@/utils/pylon-init";
import { replaceProjectAndEnv } from "@/utils/route-utils";
import { Routes } from "@/utils/routes";
import { composeSorts, mappedCompare } from "@/utils/sort";
import { useSession } from "next-auth/react";
import { signOutAndResetAnalytics } from "@/utils/sign-out";
import { useTheme } from "next-themes";
import { useRouter } from "next/router";
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface ProjectTopRailV2Props {
  breadcrumbItems?: readonly BreadcrumbV2Item[];
  className?: string;
  /** Controls that lead the rail, ahead of the breadcrumbs. */
  leadingAccessory?: React.ReactNode;
  /**
   * Closes up the leading accessory's slot, as the global settings pages' rail collapse does: the
   * sidebar toggle's, while the pinned sidebar carries its own.
   */
  leadingAccessoryCollapsed?: boolean;
}

// The sidebar toggle's 32px button and the divider after it, with the space on either side.
const LEADING_ACCESSORY_WIDTH_PX = 32 + 6 + 1 + 15;

interface RouteMatch {
  parent?: NavbarMainItem<HRefArguments>;
  item: NavbarMainItem<HRefArguments>;
  href: string;
  parentHref?: string;
}

const getProjectBreadcrumbsRenderKey = (items: readonly BreadcrumbV2Item[]) =>
  JSON.stringify(items, (_key, value) => (typeof value === "function" ? undefined : value));

const ProjectBreadcrumbsV2 = React.memo<{
  items: readonly BreadcrumbV2Item[];
  renderKey: string;
  routeKey: string;
}>(
  ({ items }) => <BreadcrumbsV2 items={items} showDetailPanes={false} className={"!h-7 !p-0"} />,
  // Tab-only query changes rebuild callback identities, but the breadcrumb stays mounted while
  // its concrete route and visible menu content are unchanged.
  (previous, next) => previous.routeKey === next.routeKey && previous.renderKey === next.renderKey,
);

const EXPLORER_VIEW_BY_ROUTE_ID: Readonly<Record<string, string | undefined>> = {
  "explorer-online": "online",
  "explorer-offline": "offline",
  "explorer-sql": undefined,
  "sql-explorer": undefined,
};

const getExplorerRouteMatchScore = (routeId: string) => {
  if (!Object.prototype.hasOwnProperty.call(EXPLORER_VIEW_BY_ROUTE_ID, routeId)) {
    return 0;
  }

  const expectedView = EXPLORER_VIEW_BY_ROUTE_ID[routeId];
  return expectedView == null ? 1 : -1;
};

interface ProjectTopRailActionMenuItem {
  id: string;
  label: string;
  active?: boolean;
  variant?: MenuItemV2Variant;
  className?: string;
  icon?: IconName;
  trailingIcon?: IconName;
  onSelect?: () => void;
}

interface ProjectTopRailActionMenuSection {
  id: string;
  items: readonly ProjectTopRailActionMenuItem[];
}

interface ProjectTopRailAction {
  id: string;
  icon: IconName;
  label: string;
  menuSections?: readonly ProjectTopRailActionMenuSection[];
  onClick?: () => void;
}

const ACTION_MENU_WIDTH_PX = 168;
const THEME_ACTION_MENU_WIDTH_PX = 104;
const ACTION_MENU_GUTTER_PX = 4;
const ACTION_TOOLTIP_GUTTER_PX = 4;

const normalizePath = (path: string) => {
  const pathWithoutSearch = path.split(/[?#]/)[0];
  const normalized = pathWithoutSearch.replace(/\/+$/, "");
  return normalized || "/";
};

const getStringQueryParam = (value: string | string[] | undefined) =>
  Array.isArray(value) ? value[0] : value;

const getRouteParamLabel = (value: string | string[] | undefined, fallback: string | undefined) => {
  if (Array.isArray(value)) return value.join("/");
  return value ?? fallback;
};

const getDynamicRouteParamName = (segment: string) => {
  const catchAllMatch = segment.match(/^\[\[?\.\.\.(.+)\]\]?$/);
  if (catchAllMatch != null) return catchAllMatch[1];

  const match = segment.match(/^\[(.+)\]$/);
  return match?.[1];
};

const resolveNavbarHref = (
  item: NavbarMainItem<HRefArguments>,
  projectId: string | undefined,
  environmentId: string | undefined,
  context: HRefArguments,
) => {
  if (item.href == null || projectId == null || environmentId == null) return undefined;

  return item.href(projectId, environmentId, context);
};

export const findRouteMatch = ({
  links,
  path,
  projectId,
  environmentId,
  context,
}: {
  links: readonly NavbarMainItem<HRefArguments>[];
  path: string;
  projectId: string | undefined;
  environmentId: string | undefined;
  context: HRefArguments;
}): RouteMatch | undefined => {
  const normalizedPath = normalizePath(path);
  const candidates: RouteMatch[] = [];

  for (const link of links) {
    const href = resolveNavbarHref(link, projectId, environmentId, context);
    if (href != null) {
      candidates.push({ item: link, href: normalizePath(href) });
    }

    for (const subpage of link.subpages ?? []) {
      const subpageHref = resolveNavbarHref(subpage, projectId, environmentId, context);
      if (subpageHref != null) {
        candidates.push({
          parent: link,
          item: subpage,
          href: normalizePath(subpageHref),
          parentHref: href == null ? undefined : normalizePath(href),
        });
      }
    }
  }

  return candidates
    .filter((candidate) => {
      return (
        normalizedPath === candidate.href ||
        normalizedPath.startsWith(`${candidate.href}/`) ||
        (projectId != null &&
          environmentId != null &&
          candidate.item.routeMatch?.(normalizedPath, projectId, environmentId, context) === true)
      );
    })
    .sort(
      composeSorts(
        mappedCompare((candidate: RouteMatch) => candidate.href.length, "desc"),
        mappedCompare(
          (candidate: RouteMatch) => getExplorerRouteMatchScore(candidate.item.id),
          "desc",
        ),
        // Parent items often route to their first child. When those hrefs tie,
        // prefer the child candidate so breadcrumbs preserve parent -> child.
        mappedCompare((candidate: RouteMatch) => Number(candidate.parent != null), "desc"),
      ),
    )[0];
};

const getSwitcherLabel = (name: string | undefined, id: string) => name || id;
const getSwitcherMetadata = (id: string) => id;

/**
 * Feature-owned labels for id-valued route params (e.g. `{ dashboardId: "Fraud overview" }`).
 * `getDescendantRouteBreadcrumbs` prefers these over the raw URL segment; an empty-string label
 * suppresses the crumb. Merge additional feature hooks' maps here as they appear.
 */
const useRouteParamBreadcrumbLabels = (): Readonly<Record<string, string>> => {
  const dashboardLabels = useDashboardBreadcrumbLabels();
  const computeLabels = useComputeBreadcrumbLabels();
  const datasetLabels = useDatasetBreadcrumbLabels();
  const agentConversationLabels = useAgentConversationBreadcrumbLabels();
  return useMemo(
    () => ({ ...dashboardLabels, ...computeLabels, ...datasetLabels, ...agentConversationLabels }),
    [agentConversationLabels, computeLabels, dashboardLabels, datasetLabels],
  );
};

const getDisplayModeAction = (
  isDarkMode: boolean,
  theme: string | undefined,
  setTheme: (theme: string) => void,
): ProjectTopRailAction => ({
  id: "theme-toggle",
  icon: isDarkMode ? "moon" : "sun",
  label: "Display mode",
  menuSections: [
    {
      id: "theme",
      items: [
        {
          id: "theme-light",
          label: "Light",
          icon: "sun",
          active: theme === "light",
          onSelect: () => setTheme("light"),
        },
        {
          id: "theme-dark",
          label: "Dark",
          icon: "moon",
          active: theme === "dark",
          onSelect: () => setTheme("dark"),
        },
      ],
    },
    {
      id: "theme-system",
      items: [
        {
          id: "theme-system",
          label: "System",
          icon: "computer",
          active: theme === "system",
          onSelect: () => setTheme("system"),
        },
      ],
    },
  ],
});

export const ProjectDisplayModeButton: React.FC = () => {
  const { resolvedTheme, theme, setTheme } = useTheme();
  return (
    <ProjectTopRailActionButton
      action={getDisplayModeAction(resolvedTheme === "dark", theme, setTheme)}
      themeMode={resolvedTheme === "dark" ? "dark" : "light"}
    />
  );
};

const SEARCH_SHORTCUT_KEYS = ["⌘", "K"] as const;

const NAVIGATION_MODES: readonly SliderTabV2Item<CategoryNavMode>[] = [
  {
    id: "flag",
    label: "Flag",
    tooltipContent: "Follow the environment's onboard_experience flag",
  },
  {
    id: "classic",
    label: "Classic",
    tooltipContent: "Always the classic sidebar, in this browser",
  },
  { id: "category", label: "New", tooltipContent: "Always the category rail, in this browser" },
];

const NAV_SETUP_MENU_WIDTH_PX = 200;
// As far below its button as the account menu hangs below the avatar.
const NAV_SETUP_MENU_GUTTER_PX = 8;

/**
 * Chalk admins' switch between the two navigations, kept in this browser, at the top rail's right
 * in either one, then a "…" menu of the reader's nav setup: export, import, and reset. They show in
 * admin mode only, ending in a divider before the rail's own buttons; the switch sits on the admin
 * treatment's red, and both match the search field's 28px height.
 */
export const ProjectTopRailNavigationSwitchV2: React.FC = () => {
  const overlayPortalRoot = useOverlayPortalRoot();
  // Admin mode, as ChalkEmployeeGuard gates; its wrapper would lay a second red under the track.
  const isChalkAdmin = useDANGEROUSisChalkEmployee();
  const [mode, setMode] = useCategoryNavOverride();
  const { rearranged, reset } = useSidebarLayoutReset();
  const { exportSetup, importSetup } = useNavSetupTransfer();
  const importInputRef = useRef<HTMLInputElement | null>(null);
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ left: number; top: number } | null>(null);
  // The switch, which the navigation experiment's callout points at.
  const [switchElement, setSwitchElement] = useState<HTMLSpanElement | null>(null);
  const menuOpen = menuPosition != null;
  useEffect(() => {
    if (!menuOpen) return;
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      // The button toggles the menu itself; closing here as well would reopen it on release.
      if (menuButtonRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setMenuPosition(null);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuPosition(null);
    };
    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [menuOpen]);
  if (!isChalkAdmin) return null;

  const importFile = async (file: File) => {
    const parsed = parseNavSetup(await file.text());
    if (!parsed.ok) {
      toast.error(parsed.error);
      return;
    }
    const confirmed = await createConfirmModal({
      title: "Import nav setup",
      body: `Replace your rail and sidebar setup with the one in ${file.name}? Export yours first to keep it.`,
      removeLabel: "Import",
      presentation: "v2",
    });
    if (!confirmed) return;
    importSetup(parsed.setup);
    toast.success("Imported nav setup");
  };

  return (
    <>
      <span ref={setSwitchElement} className={"flex shrink-0"}>
        <SliderTabsV2<CategoryNavMode>
          activeItemId={mode}
          ariaLabel={"Navigation"}
          items={NAVIGATION_MODES}
          size={"sm"}
          // The track takes the admin red in place of its grey.
          className={classNames("shrink-0", CHALK_ADMIN_SURFACE_STABLE_CLASS)}
          onChange={(item) => setMode(item.id)}
        />
      </span>
      <NavExperimentCallout anchor={switchElement} />
      <IconButtonV2
        ref={menuButtonRef}
        size={"sm"}
        variant={"secondary"}
        icon={"settings"}
        aria-label={"Nav setup"}
        aria-haspopup={"menu"}
        aria-expanded={menuPosition != null}
        tooltipText={menuPosition == null ? "Nav setup" : undefined}
        tooltipPlacement={"bottom"}
        onClick={() => {
          if (menuPosition != null) {
            setMenuPosition(null);
            return;
          }
          const rect = menuButtonRef.current?.getBoundingClientRect();
          if (rect == null) return;
          setMenuPosition({
            left: rect.right - NAV_SETUP_MENU_WIDTH_PX,
            top: rect.bottom + NAV_SETUP_MENU_GUTTER_PX,
          });
        }}
      />
      {menuPosition != null &&
        typeof document !== "undefined" &&
        createPortal(
          <ViewportAwareFixedMenuV2
            ref={menuRef}
            className={"design-system-typography text-cds-md"}
            left={menuPosition.left}
            top={menuPosition.top}
            width={NAV_SETUP_MENU_WIDTH_PX}
          >
            <MenuContainerV2 className={"w-full"}>
              {/* A portable file of the whole setup, for sharing one in a trial. */}
              <MenuSectionV2>
                <MenuItemV2
                  size={"sm"}
                  label={"Export nav setup (JSON)"}
                  leftIcon={"download"}
                  onClick={() => {
                    setMenuPosition(null);
                    exportSetup();
                  }}
                />
                <MenuItemV2
                  size={"sm"}
                  label={"Import nav setup (JSON)"}
                  leftIcon={"upload"}
                  onClick={() => {
                    setMenuPosition(null);
                    importInputRef.current?.click();
                  }}
                />
              </MenuSectionV2>
              <MenuSectionV2>
                <TooltipV2 content={"No changes to reset"} placement={"left"} disabled={rearranged}>
                  {/* A disabled button fires no hover, so the tooltip reads this wrapper's. */}
                  <span className={"flex w-full"}>
                    <MenuItemV2
                      size={"sm"}
                      label={"Reset sidebar layout"}
                      leftIcon={"rotateCcw"}
                      disabled={!rearranged}
                      className={"w-full"}
                      // It undoes every rearrangement at once, so it asks first.
                      onClick={() => {
                        setMenuPosition(null);
                        void createConfirmModal({
                          title: "Reset sidebar layout",
                          body: "Put the nav back as it was: the rail's icon order, and in every sidebar its row order and moves, sections and their titles, and category names. Pinned shortcuts stay. This can't be undone.",
                          removeLabel: "Reset",
                          presentation: "v2",
                        }).then((confirmed) => {
                          if (confirmed) reset();
                        });
                      }}
                    />
                  </span>
                </TooltipV2>
              </MenuSectionV2>
            </MenuContainerV2>
          </ViewportAwareFixedMenuV2>,
          overlayPortalRoot ?? document.body,
        )}
      <input
        ref={importInputRef}
        type={"file"}
        accept={"application/json,.json"}
        aria-hidden={true}
        tabIndex={-1}
        className={"hidden"}
        data-testid={"nav-setup-import-input"}
        onChange={(event) => {
          const file = event.currentTarget.files?.[0];
          // Cleared, so choosing the same file again still imports it.
          event.currentTarget.value = "";
          if (file != null) void importFile(file);
        }}
      />
      {/* Sets these admin controls apart from the rail's own buttons. */}
      <div
        aria-hidden={true}
        className={"h-5 w-px shrink-0 bg-cds-surface-border dark:bg-white/10"}
      />
    </>
  );
};

/** Offers the current team's support channel beside search. */
const ProjectTopRailFeedbackButtonV2: React.FC = () => {
  const pylonEnabled = usePylonEnabled();
  const { markChatBubbleVisible } = usePylonChatBubbleControls();
  // Pylon shows one window at a time, so the button waits while one is open.
  const pylonWindowOpen = usePylonWindowOpen();

  if (!pylonEnabled) {
    return (
      <LinkButtonV2
        className="mr-cds-4 shrink-0"
        icon="messageSquare"
        size="sm"
        variant="secondary"
        href={COMMUNITY_SLACK_URL}
        openInNewTab
      >
        Community Slack
      </LinkButtonV2>
    );
  }

  return (
    <ButtonV2
      // 4px on top of the row's 8px gap sets it a little apart from search.
      className="mr-cds-4 shrink-0"
      icon="messageSquare"
      size="sm"
      type="button"
      variant="secondary"
      disabled={pylonWindowOpen}
      onClick={() => {
        ensurePylonWindowCallbacks();
        markChatBubbleVisible();
        window.Pylon?.("showTicketForm", "submit-a-request");
      }}
    >
      Submit feedback
    </ButtonV2>
  );
};

/**
 * The category nav's search: opens the command palette; drawn as a search field so it reads as the place to type. The
 * keycaps sit as far from the field's right edge as from its top and bottom: 16px keys in a 28px
 * field leave 6px, and the 5px right padding plus the 1px border make the same 6px.
 */
const ProjectTopRailSearchV2: React.FC = () => (
  <button
    type={"button"}
    aria-keyshortcuts={"Meta+K"}
    aria-label={"Search"}
    className={classNames(
      "group flex h-7 w-60 min-w-0 shrink items-center gap-cds-6 rounded-cds-6 pl-cds-8 pr-[5px]",
      "border-[1px] border-black/[0.08] bg-cds-surface-default text-left transition-colors",
      "hover:border-black/[0.16] focus:outline-none focus-visible:shadow-cds-focus",
      "dark:border-white/[0.08] dark:bg-white/[0.04] dark:hover:border-white/[0.16]",
    )}
    onClick={openCommandPalette}
  >
    <Icon
      aria-hidden
      name={"search"}
      size={"sm"}
      className={"shrink-0 !text-cds-ink-subtle dark:!text-zinc-500"}
    />
    <span className={"min-w-0 flex-1 truncate text-cds-sm text-cds-ink-subtle dark:text-zinc-500"}>
      Search
    </span>
    <KeyboardShortcutV2 aria-hidden keys={SEARCH_SHORTCUT_KEYS} />
  </button>
);

export const ProjectTopRailV2: React.FC<ProjectTopRailV2Props> = ({
  breadcrumbItems,
  className,
  leadingAccessory,
  leadingAccessoryCollapsed = false,
}) => {
  const router = useRouter();
  const { collapsed: railCollapsed, leaving: railLeaving } = useCategoryRailCollapse();
  const leadingAccessoryRef = useRef<HTMLDivElement | null>(null);
  const leadingAccessoryClosed = railCollapsed || leadingAccessoryCollapsed;
  const leadingAccessoryWidth = `${leadingAccessoryClosed ? 0 : LEADING_ACCESSORY_WIDTH_PX}px`;
  useTransitionAcrossPages(
    "top-rail:leading-accessory",
    leadingAccessoryRef,
    "width",
    leadingAccessoryWidth,
  );
  const { resolvedTheme, setTheme, theme } = useTheme();
  const { activeDeploymentId, environment, environmentId, loading, project, projectId, team } =
    useRouteAndTeamInfo();
  const user = useSession().data?.user;
  const projectNavbarLinks = useProjectNavbarLinks();
  const flags = useFlagsV2();
  const isEnterpriseSupport = flags.enabled("enterprise_support_enabled");
  const { CHALK_DEPLOYMENT_MODEL } = useNextEnvVars();
  const isSelfHosted = CHALK_DEPLOYMENT_MODEL === "CUSTOMER_CLOUD";
  const { escalateIssue, isCreatingIncident } = usePagerDutyEscalation();
  const pylonEnabled = usePylonEnabled();
  const { markChatBubbleVisible } = usePylonChatBubbleControls();
  const themeMode =
    resolvedTheme === "dark" ? "dark" : resolvedTheme === "light" ? "light" : undefined;
  const isDarkMode = themeMode !== "light";
  const themeClass =
    themeMode === "dark" ? "dark" : themeMode === "light" ? "theme-light" : undefined;
  const context = useMemo(
    () => ({
      activeDeploymentId,
    }),
    [activeDeploymentId],
  );
  const projectNavigationRouteKey = getProjectNavigationRouteKey(router.asPath);
  const categoryNavEnabled = useCategoryNavEnabled();
  const routeMatch = useMemo(
    () =>
      findRouteMatch({
        links: projectNavbarLinks,
        path: projectNavigationRouteKey,
        projectId,
        environmentId,
        context,
      }),
    [context, environmentId, projectId, projectNavbarLinks, projectNavigationRouteKey],
  );
  const routeParamLabels = useRouteParamBreadcrumbLabels();
  const evaluationBreadcrumb = useEvaluationBreadcrumbData();
  const notebookBreadcrumb = useNotebookBreadcrumbData();
  const breadcrumbs = useMemo(
    () =>
      buildProjectBreadcrumbs({
        currentEnvironmentId: environmentId,
        currentProjectId: projectId,
        environmentLabel: environment?.name ?? environmentId ?? "Environment",
        evaluationBreadcrumb,
        notebookBreadcrumb,
        projectLabel: project?.name ?? projectId ?? "Project",
        routeMatch,
        routeParamLabels,
        router,
        teamProjects: team?.projects ?? [],
      }),
    [
      environment?.name,
      environmentId,
      evaluationBreadcrumb,
      notebookBreadcrumb,
      project?.name,
      projectId,
      routeMatch,
      routeParamLabels,
      router,
      team?.projects,
    ],
  );
  const breadcrumbItemsToRender = breadcrumbItems ?? breadcrumbs;
  const breadcrumbRenderKey = getProjectBreadcrumbsRenderKey(breadcrumbItemsToRender);
  // Each page mounts its own top rail, whose labels and page match wait on the team and the
  // page's flags. Until both settle the trail is a stand-in (ids, or no page name) that the real
  // one replaces a moment later, so it stays hidden and fades in once it is right. When the rail
  // moves ahead of the next page (into the global settings pages), this page's trail fades out as
  // it does, so the reader sees one slide and the next trail fading in, not this one slid, then
  // swapped.
  const breadcrumbsReady = !loading && flags.ready && !railLeaving;
  const breadcrumbsNode = (
    <div
      className={classNames(
        "flex min-w-0 transition-opacity ease-out motion-reduce:transition-none",
        MOTION_DURATION_CLASS.short,
        !breadcrumbsReady && "opacity-0",
      )}
      aria-busy={!breadcrumbsReady || undefined}
    >
      <ProjectBreadcrumbsV2
        items={breadcrumbItemsToRender}
        renderKey={breadcrumbRenderKey}
        routeKey={projectNavigationRouteKey}
      />
    </div>
  );
  // The breadcrumb's last label is the page's own name, which is what Recent lists it under.
  useRecordProjectNavHistory({
    enabled: categoryNavEnabled && PROJECT_NAV_RECENT_ENABLED,
    label: breadcrumbItemsToRender.at(-1)?.label,
    icon: routeMatch?.item.icon,
  });
  const profileMenuSections = useMemo<ProjectTopRailActionMenuSection[]>(
    () => [
      {
        id: "signed-in",
        items: [
          {
            id: "signed-in-email",
            label: user?.email ?? "Signed in",
            variant: "title",
            className: "!font-normal",
          },
        ],
      },
      {
        id: "support",
        items: [
          {
            id: "docs",
            label: "Docs",
            icon: "docs",
            trailingIcon: "arrowExternal" as const,
            onSelect: () => window.open("https://docs.chalk.ai", "_blank", "noopener,noreferrer"),
          },
          {
            id: "community-slack",
            label: "Community Slack",
            icon: "messageSquare",
            trailingIcon: "arrowExternal" as const,
            onSelect: () => window.open(COMMUNITY_SLACK_URL, "_blank", "noopener,noreferrer"),
          },
          ...(pylonEnabled
            ? [
                {
                  id: "customer-portal",
                  label: "Customer portal",
                  icon: "handshake" as const,
                  trailingIcon: "arrowExternal" as const,
                  onSelect: () =>
                    window.open(
                      "https://portal.usepylon.com/chalk",
                      "_blank",
                      "noopener,noreferrer",
                    ),
                },
                {
                  id: "start-chat",
                  label: "Start chat",
                  icon: "messageCircle" as const,
                  onSelect: () => {
                    markChatBubbleVisible();
                    window.Pylon?.("showNewMessage");
                  },
                },
                {
                  id: "submit-request",
                  label: "Submit request",
                  icon: "ticket" as const,
                  onSelect: () => {
                    markChatBubbleVisible();
                    window.Pylon?.("showTicketForm", "submit-a-request");
                  },
                },
              ]
            : []),
          ...(isEnterpriseSupport && !isSelfHosted
            ? [
                {
                  id: "escalate-issue",
                  label: isCreatingIncident ? "Creating incident..." : "Escalate issue",
                  icon: "phoneArrowUpRight" as IconName,
                  onSelect: () =>
                    escalateIssue({
                      userEmail: user?.email || "Unknown User",
                      projectId,
                      environmentId,
                      currentUrl: `${window.location.origin}${router.asPath}`,
                    }),
                },
              ]
            : []),
        ],
      },
      {
        id: "account",
        items: [
          {
            id: "sign-out",
            label: "Sign out",
            icon: "logOut",
            onSelect: () => {
              void signOutAndResetAnalytics({ callbackUrl: Routes.login });
            },
          },
        ],
      },
    ],
    [
      environmentId,
      escalateIssue,
      isCreatingIncident,
      isEnterpriseSupport,
      isSelfHosted,
      markChatBubbleVisible,
      pylonEnabled,
      projectId,
      router.asPath,
      user?.email,
    ],
  );
  const actions = useMemo<ProjectTopRailAction[]>(
    () => [
      getDisplayModeAction(isDarkMode, theme, setTheme),
      {
        id: "search",
        icon: "search",
        label: "Search",
        onClick: () => {
          document.dispatchEvent(
            new KeyboardEvent("keydown", {
              bubbles: true,
              key: "k",
              metaKey: true,
            }),
          );
        },
      },
      {
        id: "profile",
        icon: "user",
        label: "Profile",
        menuSections: profileMenuSections,
      },
    ],
    [isDarkMode, profileMenuSections, setTheme, theme],
  );

  return (
    <header
      data-ds-v2={""}
      className={classNames(
        themeClass,
        "flex h-top-rail min-w-0 shrink-0 items-center justify-between gap-cds-12",
        "bg-cds-surface-active pl-cds-8 dark:bg-cds-inset-dark",
        // The category nav's 10px right inset matches the gap its 28px search field leaves above
        // and below it in the 48px rail, so the field sits evenly in the corner.
        categoryNavEnabled ? "pr-cds-10" : "pr-cds-12",
        className,
      )}
    >
      {leadingAccessory != null ? (
        <div className={"flex min-w-0 flex-1 items-center"}>
          {/* Closes up on the global settings pages, which have no sidebar to toggle, as the rail
              collapses, so the breadcrumbs slide into its place with the rail's move. */}
          <div
            ref={leadingAccessoryRef}
            className={classNames(
              "flex shrink-0 items-center overflow-hidden transition-[width]",
              CATEGORY_RAIL_COLLAPSE_TRANSITION_CLASS,
            )}
            style={{ width: leadingAccessoryWidth }}
            aria-hidden={leadingAccessoryClosed || undefined}
            inert={leadingAccessoryClosed || undefined}
          >
            {leadingAccessory}
            {/* The extra right margin matches the whitespace the toggle's 14px icon carries
                inside its 32px button, so the divider reads as centred between icon and
                breadcrumbs. */}
            <span
              aria-hidden={true}
              className={
                "ml-cds-6 mr-[15px] h-cds-16 w-px shrink-0 bg-cds-surface-border dark:bg-white/[0.08]"
              }
            />
          </div>
          {breadcrumbsNode}
        </div>
      ) : (
        breadcrumbsNode
      )}
      {categoryNavEnabled ? (
        // The display theme lives in the account menu, at this end of the rail.
        <div className={"flex shrink-0 items-center gap-cds-8"}>
          <ProjectTopRailNavigationSwitchV2 />
          <ProjectTopRailFeedbackButtonV2 />
          <ProjectTopRailSearchV2 />
          <ProjectProfileButtonV2 themeClass={themeClass} />
        </div>
      ) : (
        <div className={"flex shrink-0 items-center gap-cds-8"} aria-label={"Page actions"}>
          <ProjectTopRailNavigationSwitchV2 />
          {actions.map((action) => (
            <ProjectTopRailActionButton key={action.id} action={action} themeMode={themeMode} />
          ))}
        </div>
      )}
    </header>
  );
};

const buildProjectBreadcrumbs = ({
  currentEnvironmentId,
  currentProjectId,
  environmentLabel,
  evaluationBreadcrumb,
  notebookBreadcrumb,
  projectLabel,
  routeMatch,
  routeParamLabels,
  router,
  teamProjects,
}: {
  currentEnvironmentId: string | undefined;
  currentProjectId: string | undefined;
  environmentLabel: string;
  evaluationBreadcrumb: EvaluationBreadcrumbData | null;
  notebookBreadcrumb: NotebookBreadcrumbData | null;
  projectLabel: string;
  routeMatch: RouteMatch | undefined;
  routeParamLabels: Readonly<Record<string, string>>;
  router: ReturnType<typeof useRouter>;
  teamProjects: NonNullable<ReturnType<typeof useRouteAndTeamInfo>["team"]>["projects"];
}): BreadcrumbV2Item[] => {
  const activePage = routeMatch?.parent ?? routeMatch?.item;
  const activeSubpage = routeMatch?.parent == null ? undefined : routeMatch.item;
  const activePageHref =
    activeSubpage == null ? routeMatch?.href : routeMatch?.parentHref ?? routeMatch?.href;
  const activeSubpageHref = activeSubpage == null ? undefined : routeMatch?.href;
  const currentPath = normalizePath(router.asPath);
  const activePageHrefMatchesCurrentPath = activePageHref != null && activePageHref === currentPath;
  const routeContextBreadcrumbs = getProjectRouteContextBreadcrumbs({
    currentEnvironmentId,
    currentProjectId,
    evaluationBreadcrumb,
    notebookBreadcrumb,
    routeMatch,
    routeParamLabels,
    router,
  });
  const hasRouteContextBreadcrumbs = routeContextBreadcrumbs.length > 0;
  const currentProject = teamProjects.find((candidate) => candidate.id === currentProjectId);
  const projectSiblings = teamProjects
    .filter((candidate) => candidate.environments.length > 0)
    .toSorted((a, b) => (a.name || a.id).localeCompare(b.name || b.id))
    .map((candidate) => {
      const targetEnvironment =
        candidate.environments.find((env) => env.id === currentEnvironmentId) ??
        candidate.environments[0];
      const environmentChildren = candidate.environments
        .toSorted((a, b) => (a.name || a.id).localeCompare(b.name || b.id))
        .map((environment) => ({
          id: `${candidate.id}:${environment.id}`,
          label: getSwitcherLabel(environment.name, environment.id),
          metadata: getSwitcherMetadata(environment.id),
          current: candidate.id === currentProjectId && environment.id === currentEnvironmentId,
          inactive: environment.suspendedAt != null,
          description: `Switch to ${environment.name || environment.id}.`,
          href: replaceProjectAndEnv(router, candidate.id, environment.id),
        }));

      return {
        id: candidate.id,
        label: getSwitcherLabel(candidate.name, candidate.id),
        metadata: getSwitcherMetadata(candidate.id),
        current: candidate.id === currentProjectId,
        inactive: targetEnvironment?.suspendedAt != null,
        description: "Switch projects.",
        childrenMenuSubtitle: "Environment",
        children: environmentChildren,
        href:
          targetEnvironment == null
            ? undefined
            : replaceProjectAndEnv(router, candidate.id, targetEnvironment.id),
      };
    });
  const environmentSiblings =
    currentProject?.environments
      .toSorted((a, b) => (a.name || a.id).localeCompare(b.name || b.id))
      .map((candidate) => ({
        id: candidate.id,
        label: getSwitcherLabel(candidate.name, candidate.id),
        metadata: getSwitcherMetadata(candidate.id),
        current: candidate.id === currentEnvironmentId,
        inactive: candidate.suspendedAt != null,
        description: "Switch environments.",
        href:
          currentProjectId == null
            ? undefined
            : replaceProjectAndEnv(router, currentProjectId, candidate.id),
      })) ?? [];
  const breadcrumbs: BreadcrumbV2Item[] = [
    {
      id: "project",
      label: projectLabel,
      hasDropdown: projectSiblings.length > 1,
      nonInteractive: projectSiblings.length <= 1,
      menuSubtitle: "Project",
      searchPlaceholder: "Search projects",
      siblings: projectSiblings,
    },
    {
      id: "environment",
      label: environmentLabel,
      hasDropdown: environmentSiblings.length > 1,
      nonInteractive: environmentSiblings.length <= 1,
      menuSubtitle: "Environment",
      searchPlaceholder: "Search environments",
      siblings: environmentSiblings,
    },
  ];

  if (activePage != null) {
    breadcrumbs.push({
      id: activePage.id,
      label: activePage.text,
      current: activeSubpage == null && !hasRouteContextBreadcrumbs,
      hasDropdown: false,
      href:
        (activeSubpage != null || hasRouteContextBreadcrumbs) &&
        activePageHref != null &&
        !activePageHrefMatchesCurrentPath
          ? activePageHref
          : undefined,
    });
  }

  if (activeSubpage != null) {
    breadcrumbs.push({
      id: activeSubpage.id,
      label: activeSubpage.text,
      current: !hasRouteContextBreadcrumbs,
      hasDropdown: false,
      href: hasRouteContextBreadcrumbs && activeSubpageHref != null ? activeSubpageHref : undefined,
    });
  }

  breadcrumbs.push(...routeContextBreadcrumbs);

  return breadcrumbs;
};

const getProjectRouteContextBreadcrumbs = ({
  currentEnvironmentId,
  currentProjectId,
  evaluationBreadcrumb,
  notebookBreadcrumb,
  routeMatch,
  routeParamLabels,
  router,
}: {
  currentEnvironmentId: string | undefined;
  currentProjectId: string | undefined;
  evaluationBreadcrumb: EvaluationBreadcrumbData | null;
  notebookBreadcrumb: NotebookBreadcrumbData | null;
  routeMatch: RouteMatch | undefined;
  routeParamLabels: Readonly<Record<string, string>>;
  router: ReturnType<typeof useRouter>;
}): BreadcrumbV2Item[] => {
  const evaluationBreadcrumbs = getEvaluationRouteBreadcrumbs({
    currentEnvironmentId,
    currentProjectId,
    evaluationBreadcrumb,
    router,
  });

  if (evaluationBreadcrumbs.length > 0) {
    return evaluationBreadcrumbs;
  }

  const deploymentBreadcrumbs = getDeploymentRouteBreadcrumbs({
    currentEnvironmentId,
    currentProjectId,
    router,
  });

  if (deploymentBreadcrumbs.length > 0) {
    return deploymentBreadcrumbs;
  }

  const argoWorkflowBreadcrumbs = getArgoWorkflowRouteBreadcrumbs({
    currentEnvironmentId,
    currentProjectId,
    router,
  });

  if (argoWorkflowBreadcrumbs.length > 0) {
    return argoWorkflowBreadcrumbs;
  }

  const customImageBuildBreadcrumbs = getCustomImageBuildRouteBreadcrumbs({
    currentEnvironmentId,
    currentProjectId,
    router,
  });

  if (customImageBuildBreadcrumbs.length > 0) {
    return customImageBuildBreadcrumbs;
  }

  const branchBreadcrumbs = getBranchRouteBreadcrumbs({
    currentEnvironmentId,
    currentProjectId,
    router,
  });

  if (branchBreadcrumbs.length > 0) {
    return branchBreadcrumbs;
  }

  const notebookBreadcrumbs = getNotebookRouteBreadcrumbs({
    currentEnvironmentId,
    currentProjectId,
    notebookBreadcrumb,
    router,
  });

  if (notebookBreadcrumbs.length > 0) {
    return notebookBreadcrumbs;
  }

  return getDescendantRouteBreadcrumbs({
    routeMatch,
    routeParamLabels,
    router,
  });
};

export const getEvaluationRouteBreadcrumbs = ({
  currentEnvironmentId,
  currentProjectId,
  evaluationBreadcrumb,
}: {
  currentEnvironmentId: string | undefined;
  currentProjectId: string | undefined;
  evaluationBreadcrumb: EvaluationBreadcrumbData | null;
  router: ReturnType<typeof useRouter>;
}): BreadcrumbV2Item[] => {
  if (evaluationBreadcrumb == null || currentProjectId == null || currentEnvironmentId == null) {
    return [];
  }

  const { evaluationId, evaluationName, evaluations, runId, suiteId, suiteName, suites, view } =
    evaluationBreadcrumb;
  if (view === "suites") {
    return [];
  }
  const suiteHref =
    suiteId == null
      ? Routes.project.environment.evaluations(currentProjectId, currentEnvironmentId)
      : Routes.project.environment.evaluationSuite(currentProjectId, currentEnvironmentId, suiteId);
  const suiteSiblings = suites.map((suite) => ({
    id: suite.id,
    label: suite.name,
    current: suite.id === suiteId,
    href:
      suite.id === suiteId
        ? undefined
        : Routes.project.environment.evaluationSuite(
            currentProjectId,
            currentEnvironmentId,
            suite.id,
          ),
  }));
  const evaluationSiblings = evaluations.map((evaluation) => ({
    id: evaluation.id,
    label: evaluation.name,
    current: evaluation.id === evaluationId,
    href:
      evaluation.id === evaluationId
        ? undefined
        : Routes.project.environment.evaluation(
            currentProjectId,
            currentEnvironmentId,
            evaluation.id,
          ),
  }));
  const suiteHasDropdown = suiteSiblings.length > 0;
  const breadcrumbs: BreadcrumbV2Item[] = [
    {
      id: "evaluation-suite",
      label: suiteName ?? "Suite",
      current: view === "overview",
      hasDropdown: suiteHasDropdown,
      menuSubtitle: "Suite",
      searchPlaceholder: "Search suites",
      siblings: suiteSiblings,
      href: view === "overview" || suiteHasDropdown ? undefined : suiteHref,
    },
  ];

  if (view === "detail") {
    breadcrumbs.push({
      id: "evaluation",
      label: evaluationName ?? evaluationId ?? "Evaluation",
      current: true,
      hasDropdown: evaluationSiblings.length > 0,
      menuSubtitle: "Evaluation",
      searchPlaceholder: "Search evaluations",
      siblings: evaluationSiblings,
    });
  } else if (view === "run-results") {
    breadcrumbs.push({
      id: "evaluation",
      label: evaluationName ?? evaluationId ?? "Evaluation",
      current: false,
      hasDropdown: evaluationSiblings.length > 0,
      menuSubtitle: "Evaluation",
      searchPlaceholder: "Search evaluations",
      siblings: evaluationSiblings,
      href:
        evaluationId == null || evaluationSiblings.length > 0
          ? undefined
          : Routes.project.environment.evaluation(
              currentProjectId,
              currentEnvironmentId,
              evaluationId,
            ),
    });
    breadcrumbs.push({
      id: "evaluation-run",
      label: runId ?? "Run",
      current: true,
      hasDropdown: false,
    });
  }

  return breadcrumbs;
};

// The notebook crumb mirrors the environment crumb: the label resolves from
// id to the notebook TITLE once the document loads, and the dropdown lists
// the environment's notebooks with their descriptions as row subtitles
// (`metadata` — the preview pane that would show `description` is disabled
// in the top rail).
const getNotebookRouteBreadcrumbs = ({
  currentEnvironmentId,
  currentProjectId,
  notebookBreadcrumb,
  router,
}: {
  currentEnvironmentId: string | undefined;
  currentProjectId: string | undefined;
  notebookBreadcrumb: NotebookBreadcrumbData | null;
  router: ReturnType<typeof useRouter>;
}): BreadcrumbV2Item[] => {
  if (notebookBreadcrumb == null || !router.pathname.includes("/notebooks/[notebookId]")) {
    return [];
  }
  const { notebookId, title, siblings } = notebookBreadcrumb;
  const breadcrumbSiblings = siblings.map((sibling) => ({
    id: sibling.id,
    label: sibling.title,
    metadata: sibling.description.length > 0 ? sibling.description : undefined,
    current: sibling.id === notebookId,
    href:
      currentProjectId != null && currentEnvironmentId != null && sibling.id !== notebookId
        ? Routes.project.environment.notebook(currentProjectId, currentEnvironmentId, sibling.id)
        : undefined,
  }));

  return [
    {
      id: "route-param-notebookId",
      label: title ?? notebookId,
      current: true,
      hasDropdown: breadcrumbSiblings.length > 1,
      nonInteractive: breadcrumbSiblings.length <= 1,
      menuSubtitle: "Notebook",
      searchPlaceholder: "Search notebooks",
      siblings: breadcrumbSiblings,
    },
  ];
};

const getArgoWorkflowRouteBreadcrumbs = ({
  currentEnvironmentId,
  currentProjectId,
  router,
}: {
  currentEnvironmentId: string | undefined;
  currentProjectId: string | undefined;
  router: ReturnType<typeof useRouter>;
}): BreadcrumbV2Item[] => {
  const workflowName = getStringQueryParam(router.query.workflowName);
  if (workflowName == null) {
    return [];
  }

  const pathname = normalizePath(router.pathname);
  if (!pathname.includes("/argo-workflows/[workflowName]")) {
    return [];
  }

  // Only the leaf crumb: the "Build workflows" section crumb is already provided
  // (and made clickable to the list) by the active subpage, so adding a list crumb
  // here would duplicate it.
  return [
    {
      id: "build-workflow-id",
      label: workflowName,
      current: true,
      hasDropdown: false,
    },
  ];
};

const getCustomImageBuildRouteBreadcrumbs = ({
  currentEnvironmentId,
  currentProjectId,
  router,
}: {
  currentEnvironmentId: string | undefined;
  currentProjectId: string | undefined;
  router: ReturnType<typeof useRouter>;
}): BreadcrumbV2Item[] => {
  const contentHash = getStringQueryParam(router.query.contentHash);
  if (contentHash == null) {
    return [];
  }

  const pathname = normalizePath(router.pathname);
  if (!pathname.includes("/images/[contentHash]")) {
    return [];
  }

  // Only the leaf crumb: the "Images" section crumb is already provided (and made
  // clickable to the list) by the active subpage.
  return [
    {
      id: "custom-image-build-id",
      // Content hashes are long; show a readable prefix in the breadcrumb.
      label: contentHash.length > 16 ? contentHash.slice(0, 16) : contentHash,
      current: true,
      hasDropdown: false,
    },
  ];
};

const getBranchRouteBreadcrumbs = ({
  currentEnvironmentId,
  currentProjectId,
  router,
}: {
  currentEnvironmentId: string | undefined;
  currentProjectId: string | undefined;
  router: ReturnType<typeof useRouter>;
}): BreadcrumbV2Item[] => {
  const branchName = getStringQueryParam(router.query.branchName);
  if (currentEnvironmentId == null || currentProjectId == null || branchName == null) {
    return [];
  }

  const pathname = normalizePath(router.pathname);
  if (!pathname.includes("/branches/[branchName]")) {
    return [];
  }

  return [
    {
      id: "branch-id",
      label: branchName,
      current: pathname.endsWith("/branches/[branchName]"),
      hasDropdown: false,
      href: pathname.endsWith("/branches/[branchName]")
        ? undefined
        : Routes.project.environment.branch(currentProjectId, currentEnvironmentId, branchName),
    },
  ];
};

export const getDescendantRouteBreadcrumbs = ({
  routeMatch,
  routeParamLabels,
  router,
}: {
  routeMatch: RouteMatch | undefined;
  routeParamLabels: Readonly<Record<string, string>>;
  router: ReturnType<typeof useRouter>;
}): BreadcrumbV2Item[] => {
  if (routeMatch == null) return [];

  const currentPath = normalizePath(router.asPath);
  // Conversation URLs remain stable even though their directory lives in Settings.
  const conversationRoot = currentPath.match(
    /^(\/projects\/[^/]+\/environments\/[^/]+\/agent-conversations)(?:\/|$)/,
  )?.[1];
  const baseHref =
    routeMatch.item.id === "assistant" && conversationRoot != null
      ? conversationRoot
      : normalizePath(routeMatch.href);

  if (currentPath === baseHref || !currentPath.startsWith(`${baseHref}/`)) {
    return [];
  }

  const baseSegmentCount = baseHref.split("/").filter(Boolean).length;
  const currentSegments = currentPath.split("/").filter(Boolean);
  const suffixSegments = currentSegments.slice(baseSegmentCount);
  const suffixPatternSegments = normalizePath(router.pathname)
    .split("/")
    .filter(Boolean)
    .slice(baseSegmentCount);
  const breadcrumbs: BreadcrumbV2Item[] = [];

  let consumedSuffixSegmentCount = 0;
  for (const patternSegment of suffixPatternSegments) {
    const paramName = getDynamicRouteParamName(patternSegment);
    const isCatchAll = patternSegment.includes("...");

    if (paramName == null) {
      consumedSuffixSegmentCount += 1;
      continue;
    }

    const consumedByParam = isCatchAll
      ? Math.max(suffixSegments.length - consumedSuffixSegmentCount, 1)
      : 1;
    const fallbackLabel = suffixSegments
      .slice(consumedSuffixSegmentCount, consumedSuffixSegmentCount + consumedByParam)
      .join("/");
    const label =
      routeParamLabels[paramName] ?? getRouteParamLabel(router.query[paramName], fallbackLabel);
    consumedSuffixSegmentCount += consumedByParam;

    if (label == null || label.length === 0) {
      continue;
    }

    const href = `/${currentSegments
      .slice(0, baseSegmentCount + consumedSuffixSegmentCount)
      .join("/")}`;

    breadcrumbs.push({
      id: `route-param-${paramName}`,
      label,
      current: false,
      hasDropdown: false,
      href,
    });

    if (isCatchAll) {
      break;
    }
  }

  return breadcrumbs.map((breadcrumb, index) => {
    const current = index === breadcrumbs.length - 1;

    return {
      ...breadcrumb,
      current,
      href: current ? undefined : breadcrumb.href,
    };
  });
};

const getDeploymentRouteBreadcrumbs = ({
  currentEnvironmentId,
  currentProjectId,
  router,
}: {
  currentEnvironmentId: string | undefined;
  currentProjectId: string | undefined;
  router: ReturnType<typeof useRouter>;
}): BreadcrumbV2Item[] => {
  const deploymentId = getStringQueryParam(router.query.deploymentId);
  if (currentEnvironmentId == null || currentProjectId == null || deploymentId == null) {
    return [];
  }

  const pathname = normalizePath(router.pathname);
  const branchName = getStringQueryParam(router.query.branchName);
  const featureFqn = getStringQueryParam(router.query.featureFqn);
  const resolverFqn = getStringQueryParam(router.query.resolverFqn);
  const isBranchDeploymentRoute = pathname.includes(
    "/branches/[branchName]/deployments/[deploymentId]",
  );
  const isDeploymentRoute =
    isBranchDeploymentRoute || pathname.includes("/deployments/[deploymentId]");

  if (!isDeploymentRoute) {
    return [];
  }

  const breadcrumbs: BreadcrumbV2Item[] = [];
  const deploymentHref =
    isBranchDeploymentRoute && branchName != null
      ? Routes.project.environment.branchDeployment(
          currentProjectId,
          currentEnvironmentId,
          branchName,
          deploymentId,
        )
      : Routes.project.environment.deployment(currentProjectId, currentEnvironmentId, deploymentId);

  if (isBranchDeploymentRoute && branchName != null) {
    breadcrumbs.push({
      id: "branch-id",
      label: branchName,
      current: false,
      hasDropdown: false,
      href: Routes.project.environment.branch(currentProjectId, currentEnvironmentId, branchName),
    });
  }

  breadcrumbs.push({
    id: "deployment-id",
    label: deploymentId,
    current: isDeploymentOverviewRoute(pathname),
    hasDropdown: false,
    href: isDeploymentOverviewRoute(pathname) ? undefined : deploymentHref,
  });

  const terminalBreadcrumb = getDeploymentTerminalBreadcrumb({
    featureFqn,
    pathname,
    resolverFqn,
  });

  if (terminalBreadcrumb != null) {
    breadcrumbs.push(terminalBreadcrumb);
  }

  return breadcrumbs;
};

const isDeploymentOverviewRoute = (pathname: string) =>
  pathname.endsWith("/deployments/[deploymentId]");

const getDeploymentTerminalBreadcrumb = ({
  featureFqn,
  pathname,
  resolverFqn,
}: {
  featureFqn: string | undefined;
  pathname: string;
  resolverFqn: string | undefined;
}): BreadcrumbV2Item | undefined => {
  if (pathname.endsWith("/source")) {
    return {
      id: "deployment-source",
      label: "Source code",
      current: true,
      hasDropdown: false,
    };
  }

  if (pathname.includes("/features")) {
    return {
      id: "deployment-features",
      label: featureFqn ?? "Features",
      current: true,
      hasDropdown: false,
    };
  }

  if (pathname.includes("/resolvers")) {
    return {
      id: "deployment-resolvers",
      label: resolverFqn ?? "Resolvers",
      current: true,
      hasDropdown: false,
    };
  }

  if (pathname.endsWith("/compare")) {
    return {
      id: "deployment-compare",
      label: "Compare",
      current: true,
      hasDropdown: false,
    };
  }
};

const ProjectTopRailActionButton: React.FC<{
  action: ProjectTopRailAction;
  themeMode?: "light" | "dark";
}> = ({ action, themeMode }) => {
  const overlayPortalRoot = useOverlayPortalRoot();
  const isDarkMode = themeMode !== "light";
  const themeClass =
    themeMode === "dark" ? "dark" : themeMode === "light" ? "theme-light" : undefined;
  const tooltipId = useId();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const actionMenuRef = useRef<HTMLDivElement | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{
    left: number;
    top: number;
  } | null>(null);
  const [actionMenuPosition, setActionMenuPosition] = useState<{
    left: number;
    top: number;
  } | null>(null);
  const isSearchAction = action.id === "search";
  const isThemeToggle = action.id === "theme-toggle";
  const isProfileAction = action.id === "profile";
  const hasTooltip = isSearchAction || isThemeToggle || isProfileAction;
  const tooltipLabel = isProfileAction ? "Account" : action.label;
  const actionMenuOpen = actionMenuPosition != null;
  const actionMenuWidth = isThemeToggle ? THEME_ACTION_MENU_WIDTH_PX : ACTION_MENU_WIDTH_PX;

  const showTooltip = () => {
    if (!hasTooltip) return;

    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;

    setTooltipPosition({
      left: rect.left + rect.width / 2,
      top: rect.bottom + ACTION_TOOLTIP_GUTTER_PX,
    });
  };
  const hideTooltip = () => setTooltipPosition(null);
  const closeActionMenu = useCallback(() => setActionMenuPosition(null), []);
  const toggleActionMenu = () => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;

    setActionMenuPosition((prev) =>
      prev == null
        ? {
            left: isThemeToggle
              ? rect.left + rect.width / 2 - actionMenuWidth / 2
              : rect.right - actionMenuWidth,
            top: rect.bottom + ACTION_MENU_GUTTER_PX,
          }
        : null,
    );
  };

  useEffect(() => {
    if (!actionMenuOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (buttonRef.current?.contains(target) || actionMenuRef.current?.contains(target)) return;

      closeActionMenu();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeActionMenu();
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [actionMenuOpen, closeActionMenu]);

  return (
    <>
      <IconButtonV2
        ref={buttonRef}
        size={"xs"}
        iconSize={"md"}
        variant={"tertiary"}
        icon={action.icon}
        aria-label={action.label}
        aria-describedby={tooltipPosition ? tooltipId : undefined}
        aria-haspopup={action.menuSections ? "menu" : undefined}
        aria-expanded={action.menuSections ? actionMenuOpen : undefined}
        onBlur={hideTooltip}
        onFocus={showTooltip}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onClick={() => {
          if (action.menuSections != null) {
            hideTooltip();
            toggleActionMenu();
            return;
          }

          action.onClick?.();
        }}
        className={classNames(
          "!h-6 !w-6 !rounded-full",
          isDarkMode
            ? [
                "!bg-white/[0.08] !text-zinc-500 dark:!text-zinc-500",
                "hover:!bg-white/[0.08] active:!bg-white/[0.08]",
                "hover:!text-zinc-400 dark:hover:!text-zinc-400",
                "[&_svg]:!text-zinc-500 dark:[&_svg]:!text-zinc-500",
                "[&:hover_svg]:!text-zinc-400 dark:[&:hover_svg]:!text-zinc-400",
                actionMenuOpen &&
                  "!text-zinc-400 dark:!text-zinc-400 [&_svg]:!text-zinc-400 dark:[&_svg]:!text-zinc-400",
              ]
            : [
                "!bg-cds-surface-border !text-cds-ink-subtle",
                "hover:!bg-cds-surface-border active:!bg-cds-surface-border",
                "hover:!text-cds-ink-medium",
                "[&_svg]:!text-cds-ink-subtle",
                "[&:hover_svg]:!text-cds-ink-medium",
                actionMenuOpen && "!text-cds-ink-medium [&_svg]:!text-cds-ink-medium",
              ],
        )}
      />
      {actionMenuPosition != null &&
        typeof document !== "undefined" &&
        createPortal(
          <ViewportAwareFixedMenuV2
            ref={actionMenuRef}
            className={themeClass}
            left={actionMenuPosition.left}
            top={actionMenuPosition.top}
            width={actionMenuWidth}
            onMouseLeave={closeActionMenu}
          >
            <MenuContainerV2
              className={
                "w-full max-h-[var(--viewport-aware-fixed-menu-max-height)] overflow-y-auto"
              }
            >
              {action.menuSections?.map((section) => (
                <MenuSectionV2 key={section.id}>
                  {section.items.map((item) => (
                    <MenuItemV2
                      key={item.id}
                      size={"sm"}
                      variant={item.variant}
                      label={item.label}
                      active={item.active}
                      className={item.className}
                      leftIcon={item.variant === "title" ? undefined : item.icon}
                      rightIcon={item.variant === "title" ? undefined : item.trailingIcon}
                      onClick={
                        item.variant === "title"
                          ? undefined
                          : () => {
                              item.onSelect?.();
                              closeActionMenu();
                            }
                      }
                    />
                  ))}
                </MenuSectionV2>
              ))}
            </MenuContainerV2>
          </ViewportAwareFixedMenuV2>,
          overlayPortalRoot ?? document.body,
        )}
      {tooltipPosition != null &&
        typeof document !== "undefined" &&
        createPortal(
          <ViewportAwareShortcutTooltipV2
            id={tooltipId}
            themeClass={themeClass}
            left={tooltipPosition.left}
            top={tooltipPosition.top}
            label={tooltipLabel}
            keys={isSearchAction ? [{ value: "⌘", ariaLabel: "Command" }, { value: "K" }] : []}
          />,
          overlayPortalRoot ?? document.body,
        )}
    </>
  );
};
